import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import InputMask from "react-input-mask";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ConfirmationArea } from "../../components/confirmationArea/ConfirmationArea";
import { PageCard } from "../../components/pageCard/PageCard";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { IEvaluation } from "../../interfaces/IEvaluation";
import { IExhibition } from "../../interfaces/IExhibition";
import { IJudgmentCriteria } from "../../interfaces/IJudgmentCriteria";
import { IPageProps } from "../../interfaces/IPageProps";
import { IPlant } from "../../interfaces/IPlant";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";
import {
  BoxCriteria,
  BoxImage,
  TabPanelActions,
  TabPanelContainer,
  TabPanelContent,
} from "./ExhibitionEvaluation.styles";

const sortJudgments = (judgments: IExhibition["judgments"] = []) =>
  judgments
    .slice()
    .sort((a, b) => (a.category?.sequence ?? 0) - (b.category?.sequence ?? 0));

const sortPlantsByNumber = (plants: IPlant[] = []) =>
  plants.slice().sort((a, b) => Number(a?.number) - Number(b?.number));

const sortCriteriaListByCriteriaSequenceNumber = (
  criteriaList: IJudgmentCriteria[] = [],
) =>
  criteriaList
    .slice()
    .sort((a, b) => (a.criteria?.sequence ?? 0) - (b.criteria?.sequence ?? 0));

const PlantEmage: React.FC<{
  plant: IPlant;
}> = ({ plant }) => {
  return (
    <BoxImage>
      {plant.plantImages && plant.plantImages.length > 0 ? (
        <img
          src={plant.plantImages[0].file}
          alt={plant.name}
          style={{ maxWidth: "250px", maxHeight: "100%", borderRadius: "8px" }}
        />
      ) : (
        <Typography variant="body2" color="textSecondary">
          Sem imagem disponível
        </Typography>
      )}
    </BoxImage>
  );
};

export const ExhibitionEvaluation: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const toastr = useToastr();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentJudgmentId, setCurrentJudgmentId] = useState("");
  const [judgmentPlants, setJudgmentPlants] = useState<IPlant[]>([]);
  const [judgmentCriteriaList, setJudgmentCriteriaList] = useState<
    IJudgmentCriteria[]
  >([]);
  const [exhibition, setExhibition] = useState<IExhibition | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeStep, setActiveStep] = useState(0);
  const [openDialogFinishJudgment, setOpenDialogFinishJudgment] =
    useState(false);
  const [scores, setScores] = useState<{ [criteriaId: string]: number }>({});
  const [formErrors, setFormErrors] = useState<{
    [criteriaId: string]: string;
  }>({});
  const [openReferenceDialog, setOpenReferenceDialog] = useState(false);
  const [referenceData, setReferenceData] = useState<{
    voteMinimumScore: IEvaluation | null;
    voteMaximumScore: IEvaluation | null;
  } | null>(null);

  const sortedJudgments = useMemo(
    () => sortJudgments(exhibition?.judgments),
    [exhibition],
  );

  const currentJudgmentIndex = useMemo(
    () => sortedJudgments.findIndex(j => String(j.id) === currentJudgmentId),
    [sortedJudgments, currentJudgmentId],
  );

  const loadExhibition = useCallback(async () => {
    const id = location.pathname
      .replace("/exhibition-evaluation", "")
      .replace("/", "");
    if (!id) return;

    setLoading(true);
    try {
      const response = await api.get(`/exhibitions/complete/${id}`);
      const exhibition: IExhibition = response.data;
      setExhibition(exhibition);

      try {
        await api.post(`/exhibitions/${id}/evaluation/start`);
      } catch (err) {
        console.error("Erro ao registrar início do julgamento", err);
      }

      let found = false;
      let initialJudgmentId = "";
      let initialStep = 0;
      let initialScores: { [criteriaId: string]: number } = {};

      const sortedJudgments = sortJudgments(exhibition.judgments);

      for (let j = 0; j < sortedJudgments.length && !found; j++) {
        const judgment = sortedJudgments[j];
        const sortedPlants = sortPlantsByNumber(judgment.plants);

        for (let p = 0; p < sortedPlants.length; p++) {
          const plant = sortedPlants[p];
          if (
            !plant?.evaluations ||
            plant?.evaluations.length < (judgment.judgmentCriteria?.length || 0)
          ) {
            initialJudgmentId = String(judgment.id);
            initialStep = p;
            initialScores = {};
            if (plant?.evaluations) {
              initialScores = plant.evaluations.reduce(
                (acc, ev) => {
                  acc[ev.judgmentCriteriaId] = ev.score;
                  return acc;
                },
                {} as { [criteriaId: string]: number },
              );
            }
            found = true;
            break;
          }
        }
      }

      if (!found && sortedJudgments.length > 0) {
        const lastJudgment = sortedJudgments[sortedJudgments.length - 1];
        const lastPlants = sortPlantsByNumber(lastJudgment.plants);
        const lastPlant = lastPlants[lastPlants.length - 1];
        initialJudgmentId = String(lastJudgment.id);
        initialStep = lastPlants.length - 1;
        initialScores = {};
        if (lastPlant?.evaluations) {
          lastPlant.evaluations.forEach(ev => {
            initialScores[ev.judgmentCriteriaId] = ev.score;
          });
        }
      }

      setCurrentJudgmentId(initialJudgmentId);
      setActiveStep(initialStep);
      setScores(initialScores);
    } catch (error: any) {
      if (error.status === 401) {
        signOut();
        navigate("/");
      }
      toastr.error(
        error.response?.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [navigate, signOut, toastr, location.pathname]);

  const loadExhibitionAtStep = useCallback(
    async (judgmentId: string, step: number) => {
      const id = location.pathname
        .replace("/exhibition-evaluation", "")
        .replace("/", "");
      if (!id) return;

      setLoading(true);
      try {
        const response = await api.get(`/exhibitions/complete/${id}`);
        const exhibition: IExhibition = response.data;
        setExhibition(exhibition);

        const sortedJudgments = sortJudgments(exhibition.judgments);
        const currentJudgment = sortedJudgments.find(
          j => String(j.id) === judgmentId,
        );
        const sortedPlants = sortPlantsByNumber(currentJudgment?.plants);

        let initialScores: { [criteriaId: string]: number } = {};
        const plant = sortedPlants[step];
        if (plant?.evaluations) {
          plant.evaluations.forEach(ev => {
            initialScores[ev.judgmentCriteriaId] = ev.score;
          });
        }

        setCurrentJudgmentId(String(currentJudgment?.id));
        setActiveStep(step);
        setScores(initialScores);
      } catch (error: any) {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }
        toastr.error(
          error.response?.data?.message || "Contate a equipe de suporte",
        );
      } finally {
        setLoading(false);
      }
    },
    [navigate, signOut, toastr, location.pathname],
  );

  useEffect(() => {
    if (!currentJudgmentId) return;
    const currentJudgment = sortedJudgments.find(
      j => String(j.id) === currentJudgmentId,
    );
    setJudgmentPlants(sortPlantsByNumber(currentJudgment?.plants));
    setJudgmentCriteriaList(
      sortCriteriaListByCriteriaSequenceNumber(
        currentJudgment?.judgmentCriteria,
      ),
    );
  }, [currentJudgmentId, sortedJudgments]);

  useEffect(() => {
    const plant = judgmentPlants[activeStep];
    let plantScores: { [criteriaId: string]: number } = {};
    if (plant && plant.evaluations && Array.isArray(plant.evaluations)) {
      plant.evaluations.forEach(ev => {
        plantScores[ev.judgmentCriteriaId] = ev.score;
      });
    }
    setScores(plantScores);
  }, [judgmentPlants, activeStep]);

  useEffect(() => {
    loadExhibition();
  }, [loadExhibition]);

  const handleChangeTab = (_event: SyntheticEvent, newValue: string) => {
    loadExhibitionAtStep(newValue, 0);
  };

  const handleNextCategory = useCallback(() => {
    if (currentJudgmentIndex === sortedJudgments.length - 1) {
      setOpenDialogFinishJudgment(true);
      return;
    }
    const nextJudgment = sortedJudgments[currentJudgmentIndex + 1];
    if (nextJudgment) {
      loadExhibitionAtStep(String(nextJudgment.id), 0);
    }
  }, [currentJudgmentIndex, sortedJudgments, loadExhibitionAtStep]);

  const handleNext = useCallback(() => {
    const nextIndex = activeStep + 1;
    if (nextIndex >= judgmentPlants.length) {
      handleNextCategory();
    } else {
      setActiveStep(nextIndex);
    }
    window.scrollTo(0, 0);
  }, [activeStep, judgmentPlants.length, handleNextCategory]);

  const handleBack = useCallback(() => {
    const prevIndex = activeStep - 1;
    if (prevIndex < 0) {
      if (currentJudgmentIndex === 0) return;

      const prevJudgment = sortedJudgments[currentJudgmentIndex - 1];
      const prevPlants = sortPlantsByNumber(prevJudgment.plants);
      const lastStep = prevPlants.length - 1;
      loadExhibitionAtStep(String(prevJudgment.id), lastStep);
    } else {
      loadExhibitionAtStep(currentJudgmentId, prevIndex);
    }
    window.scrollTo(0, 0);
  }, [
    activeStep,
    currentJudgmentId,
    currentJudgmentIndex,
    sortedJudgments,
    loadExhibitionAtStep,
  ]);

  const handleScoreChange = (criteriaId: string, value: number) => {
    setScores(prev => ({
      ...prev,
      [criteriaId]: value,
    }));
  };

  const handleNextWithSave = useCallback(async () => {
    const evaluations: IEvaluation[] = judgmentCriteriaList
      .filter(
        judgmentCriteria =>
          judgmentCriteria.id !== undefined &&
          judgmentPlants[activeStep]?.id !== undefined,
      )
      .map(judgmentCriteria => ({
        judgmentCriteriaId: String(judgmentCriteria.id),
        score:
          judgmentCriteria.id !== undefined
            ? Number(scores[judgmentCriteria.id] ?? 0)
            : 0,
        plantId: String(judgmentPlants[activeStep].id),
        plant: judgmentPlants[activeStep],
      }));

    if (evaluations.length === 0) {
      handleNext();
      return;
    }
    try {
      setFormErrors({});
      const schemaFields: Record<string, any> = {};
      judgmentCriteriaList.forEach(judgmentCriteria => {
        if (judgmentCriteria.id !== undefined) {
          schemaFields[judgmentCriteria.id] = Yup?.number()
            .typeError("A nota deve ser um número")
            .required("Campo obrigatório")
            .min(1, "A nota deve ser maior que 0")
            .max(
              judgmentCriteria.maximum ?? 99,
              `A nota deve ser menor ou igual a ${
                judgmentCriteria.maximum ?? 99
              }`,
            );
        }
      });

      const schema = Yup.object().shape(schemaFields);

      const scoresToValidate: Record<string, any> = {};
      evaluations.forEach(ev => {
        scoresToValidate[ev.judgmentCriteriaId] = ev.score;
      });

      await schema.validate(scoresToValidate, { abortEarly: false });

      await api.post("/evaluations", { evaluations });
      toastr.success("Avaliações salvas com sucesso!");
      handleNext();
    } catch (error: any) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors = getValidationError(error);
        setFormErrors(validationErrors);
        toastr.error("Há campos com valores inválidos.");
      } else {
        toastr.error(
          error.response?.data?.message || "Erro ao salvar avaliações",
        );
      }
    }
  }, [
    judgmentCriteriaList,
    scores,
    judgmentPlants,
    activeStep,
    handleNext,
    toastr,
  ]);

  const handleListReferences = useCallback(
    async (criteriaId: string) => {
      try {
        const response = await api.post(`/evaluations/references`, {
          criteriaId,
          specieId:
            sortedJudgments[currentJudgmentIndex].category?.specieId || null,
          exhibitionId: exhibition?.id,
        });

        const references = response.data;
        setReferenceData({
          voteMinimumScore: {
            ...references.voteMinimumScore,
            plant: references.voteMinimumScore?.plant || null,
          },
          voteMaximumScore: {
            ...references.voteMaximumScore,
            plant: references.voteMaximumScore?.plant || null,
          },
        });
        setOpenReferenceDialog(true);
      } catch (error: any) {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }
        toastr.error(
          error.response?.data?.message || "Contate a equipe de suporte",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      navigate,
      signOut,
      toastr,
      currentJudgmentIndex,
      exhibition?.id,
      sortedJudgments,
    ],
  );

  return (
    <>
      <TitleContainer>Julgamento</TitleContainer>
      <PageCard>
        <Box>
          <TabContext value={currentJudgmentId}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList
                onChange={handleChangeTab}
                aria-label="lab API tabs example"
                variant="scrollable"
                scrollButtons="auto"
              >
                {sortedJudgments.map(judgment => (
                  <Tab
                    key={judgment.id}
                    label={
                      `${judgment.category?.sequence} - ${judgment.category?.name}` ||
                      "Sem categoria"
                    }
                    value={String(judgment.id)}
                  />
                ))}
              </TabList>
            </Box>
            {sortedJudgments.map(judgment => (
              <TabPanel key={judgment.id} value={String(judgment.id)}>
                <TabPanelContainer>
                  {judgmentPlants.length > 0 ? (
                    <>
                      <TabPanelContent>
                        <Grid container spacing={2} width={"100%"}>
                          <Grid item lg={6} md={6} sm={6} xs={12}>
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="subtitle1"
                                color="text.secondary"
                              >
                                Número:
                              </Typography>
                              <Typography
                                variant="h5"
                                fontWeight={600}
                                gutterBottom
                              >
                                {judgmentPlants[activeStep]?.number}
                              </Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="subtitle1"
                                color="text.secondary"
                              >
                                Nome:
                              </Typography>
                              <Typography
                                variant="h5"
                                fontWeight={600}
                                gutterBottom
                              >
                                {judgmentPlants[activeStep]?.name}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item lg={6} md={6} sm={6} xs={12}>
                            <PlantEmage
                              plant={judgmentPlants[activeStep] || {}}
                            />
                          </Grid>
                        </Grid>
                      </TabPanelContent>
                      <TabPanelActions>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handleBack}
                          disabled={
                            currentJudgmentIndex === 0 && activeStep === 0
                          }
                        >
                          Anterior
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleNextWithSave}
                          disabled={loading}
                        >
                          Próximo
                        </Button>
                      </TabPanelActions>
                      <TabPanelContent>
                        <Grid
                          container
                          justifyContent="center"
                          sx={{
                            mt: 2,
                            mb: 2,
                            width: "100%",
                            maxWidth: { xs: "100%", md: "50%" },
                            mx: "auto",
                          }}
                        >
                          {judgmentCriteriaList.map(judgmentCriteria => (
                            <Grid
                              item
                              xs={12}
                              key={judgmentCriteria.id}
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-start",
                                  width: "100%",
                                  marginBottom: 8,
                                }}
                              >
                                <BoxCriteria>
                                  <div>
                                    <Typography
                                      variant="subtitle1"
                                      color="text.secondary"
                                    >
                                      {judgmentCriteria.criteria?.sequence} -{" "}
                                      {judgmentCriteria.criteria?.name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ mb: 1 }}
                                    >
                                      Nota máxima: {judgmentCriteria?.maximum}
                                    </Typography>
                                  </div>

                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={() => {
                                      handleListReferences(
                                        judgmentCriteria.criteriaId,
                                      );
                                    }}
                                  >
                                    Referência
                                  </Button>
                                </BoxCriteria>
                                <InputMask
                                  mask="99"
                                  value={
                                    judgmentCriteria.id !== undefined
                                      ? scores[judgmentCriteria.id] > 0
                                        ? scores[
                                            judgmentCriteria.id
                                          ]?.toString()
                                        : ""
                                      : ""
                                  }
                                  onChange={e =>
                                    judgmentCriteria.id !== undefined &&
                                    handleScoreChange(
                                      String(judgmentCriteria.id),
                                      Number(e.target.value),
                                    )
                                  }
                                  maskPlaceholder={null}
                                >
                                  <TextField
                                    type="text"
                                    label="Nota"
                                    variant="outlined"
                                    size="small"
                                    sx={{ width: "100%" }}
                                    inputProps={{
                                      min: 1,
                                      max: judgmentCriteria.maximum,
                                    }}
                                    required
                                    helperText={
                                      judgmentCriteria.id !== undefined
                                        ? formErrors[judgmentCriteria.id]
                                        : undefined
                                    }
                                    error={
                                      judgmentCriteria.id !== undefined
                                        ? !!formErrors[judgmentCriteria.id]
                                        : false
                                    }
                                  />
                                </InputMask>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </TabPanelContent>
                    </>
                  ) : (
                    <TabPanelContent>
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        align="center"
                        width={"100%"}
                        sx={{ padding: 8 }}
                      >
                        Nenhuma planta cadastrada para esta avaliação
                      </Typography>
                    </TabPanelContent>
                  )}
                  <TabPanelActions>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleBack}
                      disabled={currentJudgmentIndex === 0 && activeStep === 0}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNextWithSave}
                      disabled={loading}
                    >
                      Próximo
                    </Button>
                  </TabPanelActions>
                </TabPanelContainer>
              </TabPanel>
            ))}
          </TabContext>
        </Box>
      </PageCard>

      {openDialogFinishJudgment && (
        <ConfirmationArea
          id="finish-judgment"
          title="Finalizar Julgamento"
          message="Você tem certeza que deseja finalizar a avaliação?"
          dialogOpen={openDialogFinishJudgment}
          deny={() => setOpenDialogFinishJudgment(false)}
          handleConfirmation={async () => {
            setOpenDialogFinishJudgment(false);
            setLoading(true);
            try {
              await api.post(`/exhibitions/${exhibition?.id}/evaluation/end`);
              toastr.success("Julgamento finalizado com sucesso!");
              navigate("/exhibitions");
            } catch (error: any) {
              toastr.error(
                error.response?.data?.message || "Erro ao finalizar o julgamento"
              );
            } finally {
              setLoading(false);
            }
          }}
        />
      )}
      {loading && <BackdropCustom />}
      {openReferenceDialog && referenceData && (
        <Dialog
          open={openReferenceDialog}
          onClose={() => setOpenReferenceDialog(false)}
          maxWidth="md"
          fullWidth
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "12px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "1.5rem",
              color: "#333",
              borderBottom: "1px solid #ddd",
              paddingBottom: "8px",
              marginBottom: "16px",
            }}
          >
            Referências de Avaliação
          </DialogTitle>
          <DialogContent
            sx={{
              padding: "16px",
              display: "flex",
              flexDirection: "row",
              gap: "16px",
            }}
          >
            <Box
              sx={{
                padding: "16px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "#555" }}
              >
                Menor Nota
              </Typography>
              <Typography variant="body1" sx={{ color: "#777" }}>
                Planta: {referenceData.voteMinimumScore?.plant?.name || "N/A"}
              </Typography>
              <Typography variant="body1" sx={{ color: "#777" }}>
                Nota: {referenceData.voteMinimumScore?.score || "N/A"}
              </Typography>
              {referenceData.voteMinimumScore?.plant?.plantImages?.[0]
                ?.file && (
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <img
                    src={
                      referenceData.voteMinimumScore.plant.plantImages[0].file
                    }
                    alt={referenceData.voteMinimumScore.plant.name}
                    style={{ maxWidth: "150px", borderRadius: "8px" }}
                  />
                </Box>
              )}
            </Box>
            <Box
              sx={{
                padding: "16px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "#555" }}
              >
                Maior Nota
              </Typography>
              <Typography variant="body1" sx={{ color: "#777" }}>
                Planta: {referenceData.voteMaximumScore?.plant?.name || "N/A"}
              </Typography>
              <Typography variant="body1" sx={{ color: "#777" }}>
                Nota: {referenceData.voteMaximumScore?.score || "N/A"}
              </Typography>
              {referenceData.voteMaximumScore?.plant?.plantImages?.[0]
                ?.file && (
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <img
                    src={
                      referenceData.voteMaximumScore.plant.plantImages[0].file
                    }
                    alt={referenceData.voteMaximumScore.plant.name}
                    style={{ maxWidth: "150px", borderRadius: "8px" }}
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: "center",
              padding: "16px",
              borderTop: "1px solid #ddd",
            }}
          >
            <Button
              onClick={() => setOpenReferenceDialog(false)}
              color="primary"
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                borderRadius: "8px",
                padding: "8px 16px",
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
